import { gql } from '@apollo/client';



export const CHECK_CATALOG_NAME_AVAILABILITY = gql`
    query CheckCatalogNameAvailability($catalogName: String!) {
        checkCatalogNameAvailability(catalogName: $catalogName)
    }
`;

// Common Fragments
export const CATALOG_BASIC_FIELDS = gql`
  fragment CatalogBasicFields on Catalog {
    id
    name
    catalogName
    description
    logo
    lastUpdated
  }
`;
export const GET_ITEM_DOWNLOAD_STATS = gql`
  query GetItemDownloadStats($itemId: ID!) {
    getItemDownloadStats(itemId: $itemId) {
      itemId
      totalDownloads
      activeLinks
      expiredLinks
      exhaustedLinks
      totalPurchases
      uniqueCustomers
      recentActivity {
        customerId
        customerName
        customerEmail
        downloadCount
        status
        createdAt
        expiresAt
      }
    }
  }
`;

export const STAT_ITEM_FIELDS = gql`
  fragment StatItemFields on ItemWithClickCount {
    id
    name
    clickCount
  }
`;

// TopItem Fragment specifically for analytics
export const TOP_ITEM_FIELDS = gql`
  fragment TopItemFields on TopItem {
    id
    name
    clickCount
    percentageDifference
  }
`;

export const CATALOG_DETAILS_FIELDS = gql`
  fragment CatalogDetailsFields on Catalog {
    ...CatalogBasicFields
    phone
    socialMedia {
      facebook
      instagram
      tiktok
      email
      website
      telegram
    }
    theme
    viewMode
    allowCheckout
    festiveMode
    onboardingStatus
    onboardingStep
    businessType
    businessCategory
    subCategories
    location
    setupCompleted
  }
  ${CATALOG_BASIC_FIELDS}
`;

export const ITEM_BASIC_FIELDS = gql`
  fragment ItemBasicFields on Item {
    id
    name
    description
    image
    order
    isVisible
    clickCount
    stockTotal
    manageInventory
    productType
  }
`;

export const CATEGORY_BASIC_FIELDS = gql`
  fragment CategoryBasicFields on Category {
    id
    name
    order
    isVisible
  }
`;

export const ITEM_PRICE_FIELDS = gql`
  fragment ItemPriceFields on Item {
    priceVariants {
      variant
      price
      stock
      compareAtPrice
      sku
      lowStockThreshold
    }
  }
`;

export const CATEGORY_WITH_ITEMS_FIELDS = gql`
  fragment CategoryWithItemsFields on Category {
    id
    name
    order
    isVisible
    items {
      ...ItemBasicFields
      ...ItemPriceFields
      category
    }
  }
  ${ITEM_BASIC_FIELDS}
  ${ITEM_PRICE_FIELDS}
`;

// Optimized Queries
export const GET_CATALOG = gql`
  query GetCatalog($id: ID!) {
    catalog(id: $id) {
      ...CatalogDetailsFields
    }
  }
  ${CATALOG_DETAILS_FIELDS}
`;

export const GET_CURRENT_USER = gql`
  query GetCurrentUser {
    getCurrentUser {
      id
      name
      email
      isGoogleAccount
      twoFactorEnabled
      isVerified
      tourStatus {
        overview
        personalization
        statistics
        catalog
        lastCompletedAt
      }
      selectedCatalog {
        ...CatalogDetailsFields
      }
    }
  }
  ${CATALOG_DETAILS_FIELDS}
`;



export const GET_USER_CATALOGS = gql`
  query GetUserCatalogs($userId: ID!) {
    getUserWithCatalogs(userId: $userId) {
      name
      catalogs {
        ...CatalogBasicFields
      }
    }
  }
  ${CATALOG_BASIC_FIELDS}
`;


// // New fields for URL-based assets
//         assetType: {
//             type: String,
//             enum: ['FILE', 'URL'],
//             default: 'FILE'
//         },
//         linkUrl: { type: String }, // For external links
//         linkDescription: { type: String }, // Description of what the link provides
//         linkType: {
//             type: String,
//             enum: ['WHATSAPP_GROUP', 'TELEGRAM_GROUP', 'DISCORD_SERVER', 'WEBSITE', 'DOWNLOAD_LINK', 'OTHER'],
//             required: function () { return this.assetType === 'URL'; }
//         }
export const GET_CATALOG_ITEMS_AND_CATEGORIES = gql`
  query GetCatalogItemsAndCategories($catalogId: ID!) {
    catalogItemsAndCategories(catalogId: $catalogId) {
      categories {
        id
        name
        order
        isVisible
        items {
          ...ItemBasicFields
          ...ItemPriceFields
          category
          digitalAssets {
            filename
            originalName
            fileSize
            fileType
            uploadedAt
            isActive
            assetType
            linkUrl
            linkDescription
            linkType

          }
          digitalSettings {
            downloadLimit
            linkExpiryHours
            allowPreview
            previewDuration
          }
        }
      }
      uncategorizedItems {
        ...ItemBasicFields
        ...ItemPriceFields
        digitalAssets {
          filename
          originalName
          fileSize
          fileType
          uploadedAt
          isActive
          assetType
          linkUrl
          linkDescription
          linkType
        }
        digitalSettings {
          downloadLimit
          linkExpiryHours
          allowPreview
          previewDuration
        }
      }
    }
  }
  ${ITEM_BASIC_FIELDS}
  ${ITEM_PRICE_FIELDS}
`;


export const COMBINED_OVERVIEW_DATA = gql`
  query CombinedOverviewData($catalogId: ID!, $days: Int!, $limit: Int!) {
    overviewData(catalogId: $catalogId, days: $days, limit: $limit) {
      performance {
        totalCategories
        totalItems
        weeklyCatalogViews
        performancePercentageChange
      }
      topItems {
        ...TopItemFields
      }
    }
    catalog(id: $catalogId) {
      id
      logo
      setupCompleted
      allowOnlinePayment
      paymentSettings {
        bankAccount {
          bankName
          accountNumber
          accountName
          bankCode
          verificationStatus
        }
      }
      shippingOptions {
        id
        name
      }
    }
    getPaymentOverview(catalogId: $catalogId, days: $days) {
      totalEarnings
      totalTransactions
      earningsPercentageChange
      transactionsPercentageChange
    }
  }
  ${TOP_ITEM_FIELDS}
`;

export const GET_CATALOG_STATISTICS = gql`
  query GetCatalogStatistics($catalogId: ID!, $days: Int!) {
    getCatalogStatistics(catalogId: $catalogId, days: $days) {
      totalCategories
      totalItems
      totalItemClicks
      performancePercentageChange
      dailyVisits {
        date
        visits
      }
      topItems {
        ...StatItemFields
      }
    }
  }
  ${STAT_ITEM_FIELDS}
`;

export const GET_CATALOG_BY_NAME = gql`
  query GetCatalogByName($catalogName: String!) {
    getCatalogByName(catalogName: $catalogName) {
      id
      catalog {
        ...CatalogBasicFields
        phone
        socialMedia {
          facebook
          instagram
          tiktok
          email
          website
          telegram
        }
        theme
        viewMode
        allowCheckout
        festiveMode
      }
      categoriesWithItems {
        id
        name
        order
        items {
          ...ItemBasicFields
          ...ItemPriceFields
        }
      }
      uncategorizedItems {
        ...ItemBasicFields
        ...ItemPriceFields
      }
    }
  }
  ${CATALOG_BASIC_FIELDS}
  ${ITEM_BASIC_FIELDS}
  ${ITEM_PRICE_FIELDS}
`;

// Analytics Queries
export const CATALOG_PERFORMANCE = gql`
  query CatalogPerformance($catalogId: ID!, $days: Int!) {
    catalogPerformance(catalogId: $catalogId, days: $days) {
      totalCategories
      totalItems
      totalItemClicks
      performancePercentageChange
      __typename
    }
  }
`;

export const TOP_ITEMS_BY_CLICKS = gql`
  query TopItemsByClicks($catalogId: ID!, $limit: Int!, $previousDays: Int!) {
    topItemsByClicks(catalogId: $catalogId, limit: $limit, previousDays: $previousDays) {
      ...TopItemFields
    }
  }
  ${TOP_ITEM_FIELDS}
`;

export const GET_VENDOR_FAQS = gql`
    query GetVendorFAQs {
      vendorFAQs {
        id
        question
        answer
      }
    }
`;

export const GET_BANK_DETAILS = gql`
  query GetBankDetails($catalogId: ID!) {
    catalog(id: $catalogId) {
      id
      allowOnlinePayment
      subscriptionPlan
      paymentSettings {
        bankAccount {
          bankName
          accountNumber
          accountName
          bankCode
          verificationStatus
          dateVerified
        }
        splitPercentage
        settlementSchedule
      }
    }
  }
`;

export const GET_TRANSACTION = gql`
  query GetTransaction($reference: String!) {
    getTransaction(reference: $reference) {
      id
      reference
      amount
      finalAmount
      vendorAmount
      digitalOnly
      status
      settlementStatus
      paymentMethod
      dateCreated
      items {
        itemId
        name
        quantity
        price
        variantName
        variantIndex
        productType
        digitalAssets {  # Add this block
          filename
          originalName
          fileSize
          fileType
          assetType
          linkUrl
          linkDescription
          linkType
        }
        digitalSettings {  # Add this block
          downloadLimit
          linkExpiryHours
          allowPreview
          previewDuration
        }
      }
      customer {
        id
        name
        email
        phone
      }
      catalog {
        id
        name
        logo
        catalogName
      }
      metadata {
        shipping {
          method
          cost
          address {
            street
            state
            country
          }
        }
        discount {
          id
          code
          type
          value
          discountAmount
          finalAmount
        }
        fees{
          paystackTransactionFee
          platformFee
        }
        notes
      }
    }
  }
`;

// Update the GET_VENDOR_TRANSACTIONS query:

export const GET_VENDOR_TRANSACTIONS = gql`
  query GetVendorTransactions(
    $catalogId: ID!
    $status: TransactionStatus
    $startDate: String
    $endDate: String
    $paymentMethod: String
    $page: Int
    $limit: Int
  ) {
    getVendorTransactions(
      catalogId: $catalogId
      status: $status
      startDate: $startDate
      endDate: $endDate
      paymentMethod: $paymentMethod
      page: $page
      limit: $limit
    ) {
      edges {
        node {
          id
          reference
          amount
          finalAmount
          vendorAmount 
          digitalOnly
          status
          settlementStatus
          paymentMethod
          dateCreated
          items {
            itemId
            name
            quantity
            price
            variantName
            productType
          }
          customer {
            id
            name
            phone
            email
          }
          metadata {
            shipping {
              method
              cost
            }
            discount {
              code
              discountAmount
            }
            notes
          }
        }
      }
      pageInfo {
        hasNextPage
        endCursor
      }
      totalCount
      totalSales
      totalVendorEarnings
      platformFees
      successfulTransactions
    }
  }
`;

export const CUSTOMER_FIELDS = gql`
  fragment CustomerFields on Customer {
    id
    name
    email
    phone
    address {
      street
      city
      state
      country
      postalCode
    }
    dateCreated
    lastPurchase
    totalPurchases
  }
`;

export const GET_CUSTOMERS = gql`
  query GetCustomers($catalogId: ID!, $search: String, $page: Int, $limit: Int) {
    getCustomers(catalogId: $catalogId, search: $search, page: $page, limit: $limit) {
      edges {
        node {
          ...CustomerFields
        }
        cursor
      }
      pageInfo {
        hasNextPage
        endCursor
      }
      totalCount
    }
  }
  ${CUSTOMER_FIELDS}
`;

export const GET_CUSTOMER_DETAILS = gql`
  query GetCustomerDetails($id: ID!) {
    getCustomerDetails(id: $id) {
      ...CustomerFields
    }
  }
  ${CUSTOMER_FIELDS}
`;

export const CHECK_EMAIL_AVAILABILITY = gql`
  query CheckEmailAvailability($email: String!) {
    checkEmailAvailability(email: $email)
  }
`;

export const GET_SETTLEMENTS = gql`
  query GetSettlements(
    $catalogId: ID!
    $status: SettlementStatus
    $startDate: String
    $endDate: String
    $page: Int
    $limit: Int
  ) {
    getSettlements(
      catalogId: $catalogId
      status: $status
      startDate: $startDate
      endDate: $endDate
      page: $page
      limit: $limit
    ) {
      edges {
        node {
          id
          reference
          amount
          status
          recipientCode
          transferCode
          dateInitiated
          dateSettled
          failureReason
        }
      }
      pageInfo {
        hasNextPage
        endCursor
      }
      totalCount
    }
  }
`;

export const GET_SETTLEMENT_OVERVIEW = gql`
  query GetSettlementOverview(
    $catalogId: ID!
    $status: SettlementStatus
    $startDate: String
    $endDate: String
    $page: Int
    $limit: Int
  ) {
    getSettlementOverview(
      catalogId: $catalogId
      status: $status
      startDate: $startDate
      endDate: $endDate
      page: $page
      limit: $limit
    ) {
      settlements {
        edges {
          node {
            id
            reference
            amount
            status
            dateInitiated
            dateSettled
            failureReason
          }
          cursor
        }
        pageInfo {
          hasNextPage
          endCursor
        }
        totalCount
      }
      completedTransactions {
        edges {
          node {
            id
            reference
            amount
            customer {
              name
              phone
            }
            settlementStatus
            dateCreated
          }
        }
        totalCount
      }
      totalSettledAmount
      pendingSettlementAmount
    }
  }
`;

export const GET_CATALOG_ITEMS = gql`
  query GetCatalogItems($catalogId: ID!) {
    getCatalogItems(catalogId: $catalogId) {
      id
      name
      description
      hasVariants
      manageInventory
      stockTotal
      priceVariants {
        variant
        price
        stock
        compareAtPrice
      }
    }
  }
`;

export const GET_SHIPPING_OPTIONS = gql`
  query GetShippingOptions($catalogId: ID!) {
    getShippingOptions(catalogId: $catalogId) {
      id
      name
      details
      estimatedDuration
      price
      isPickup
    }
  }
`;

export const GET_FREE_PLAN_LIMITS = gql`
    query GetFreePlanLimits($catalogId: ID!) {
        getFreePlanLimits(catalogId: $catalogId) {
            products {
                current
                limit
                canAdd
            }
            categories {
                current
                limit
                canAdd
            }
            customers {
                current
                limit
                canAdd
            }
            imagesPerProduct {
                limit
            }
            digitalFiles {
                maxFilesPerProduct
                maxTotalSizePerProduct
                maxFileSize
            }
        }
    }
`;
